import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { inr, num, pnlColor } from "@/lib/format";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, Play, Pause, Square, XCircle, Layers } from "lucide-react";
import { visibleInterval } from "@/lib/poll";

function Metric({ label, value, cls = "" }) {
  return (
    <div className="term-panel-2 rounded-sm p-3">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-sm font-semibold mono mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

export default function StrategyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [levels, setLevels] = useState([]);

  const load = useCallback(() => {
    api.get(`/strategies/${id}`).then((r) => setS(r.data)).catch(() => {});
    api.get(`/strategies/${id}/levels`).then((r) => setLevels(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); return visibleInterval(load, 4000); }, [load]);

  const act = async (action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try { await api.post(`/strategies/${id}/${action}`); load(); toast.success(action + " done"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <div className="p-6 text-slate-500">Loading…</div>;

  const chartData = levels.map((l) => ({ level: `L${l.level_number}`, wavg: l.weighted_average_after, exec: l.actual_spread }));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button onClick={() => nav("/strategies")} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Strategies</button>
        <div className="flex gap-1.5">
          {s.status === "draft" && <button onClick={() => act("start")} className="btn-a"><Play className="h-3 w-3" /> Start</button>}
          {s.status === "active" && <button onClick={() => act("pause")} className="btn-a"><Pause className="h-3 w-3" /> Pause Levels</button>}
          {(s.status === "paused" || s.status === "stopped") && <button onClick={() => act("start")} className="btn-a"><Play className="h-3 w-3" /> Resume</button>}
          {s.status === "active" && <button onClick={() => act("stop")} className="btn-a"><Square className="h-3 w-3" /> Stop New</button>}
          {["active", "paused", "stopped"].includes(s.status) && <button onClick={() => act("square-off", "Square off now?")} className="btn-danger" data-testid="detail-squareoff"><XCircle className="h-3 w-3" /> Square Off</button>}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <h1 className="text-xl font-bold tracking-tight">{s.name}</h1>
        <span className="text-xs text-slate-500 mono">{s.symbol} · BUY {s.buy_expiry} / SELL {s.sell_expiry} · {s.mode === "strict" ? "Strict Avg" : "Best Available"}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase ${s.status === "active" ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#1A1D24] text-slate-400"}`}>{s.status}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        <Metric label="Current Spread" value={`₹${num(s.current_spread, 2)}`} />
        <Metric label="Last Executed" value={`₹${num(s.last_executed_spread, 2)}`} />
        <Metric label="Weighted Avg" value={`₹${num(s.weighted_avg_spread, 2)}`} />
        <Metric label="Distance from Wavg" value={s.current_tolerance_pct != null ? s.current_tolerance_pct + "%" : "—"} />
        <Metric label="Next Trigger" value={s.next_trigger_spread ? `₹${s.next_trigger_spread}` : "—"} />
        <Metric label="Exit Spread" value={`₹${num(s.current_exit_spread, 2)}`} />
        <Metric label="Matched Lots" value={num(s.total_matched_lots, 0)} />
        <Metric label="Req Next-Level Lots" value={s.next_level_required_lots != null ? num(s.next_level_required_lots, 0) : "—"} />
        <Metric label="Affordable Lots" value={s.next_level_affordable_lots != null ? num(s.next_level_affordable_lots, 0) : "—"} />
        <Metric label="Deployed Margin" value={inr(s.deployed_margin)} />
        <Metric label="Gross P&L" value={s.gross_pnl != null ? inr(s.gross_pnl) : "—"} cls={pnlColor(s.gross_pnl)} />
        <Metric label="Net Est. P&L" value={s.net_pnl != null ? inr(s.net_pnl) : "—"} cls={pnlColor(s.net_pnl)} />
        <Metric label="Profit Target" value={s.profit_target_rupees ? inr(s.profit_target_rupees) : "—"} />
        <Metric label="Stop Loss" value={s.max_loss_rupees ? inr(-s.max_loss_rupees) : "—"} cls="text-[#FF6b62]" />
        <Metric label="Levels" value={`${s.levels_count}/${s.maximum_levels}`} />
        <Metric label="Days→Expiry" value={s.days_to_near_expiry != null ? s.days_to_near_expiry + "d" : "—"} cls={s.days_to_near_expiry <= 5 ? "text-[#FF6b62]" : ""} />
      </div>

      {s.status === "completed" && (
        <div className="term-panel rounded-sm p-4 text-sm flex items-center justify-between">
          <span className="text-slate-400">Strategy completed — reason: <span className="text-slate-200 mono">{s.exit_reason}</span></span>
          <span className={`font-bold mono ${pnlColor(s.realized_pnl)}`}>Realized {inr(s.realized_pnl)}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="term-panel rounded-sm p-4 lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-2"><Layers className="h-4 w-4" /> Weighted Average vs Executed Spread</div>
          {chartData.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#2A2F3A" strokeDasharray="3 3" />
                <XAxis dataKey="level" stroke="#94A3B8" fontSize={11} />
                <YAxis stroke="#94A3B8" fontSize={11} />
                <Tooltip contentStyle={{ background: "#1A1D24", border: "1px solid #2A2F3A", borderRadius: 4, fontSize: 12 }} />
                <Line type="monotone" dataKey="wavg" stroke="#2563EB" strokeWidth={2} dot name="Weighted Avg" />
                <Line type="monotone" dataKey="exec" stroke="#FFCC00" strokeWidth={2} dot name="Executed" />
              </LineChart>
            </ResponsiveContainer>
          ) : <div className="text-xs text-slate-600 py-8 text-center">No levels executed yet. Automation will place the initial matched pair when the executable spread meets your threshold.</div>}
        </div>
        <div className="term-panel rounded-sm p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Configuration</div>
          <div className="space-y-1.5 text-xs">
            {[["Trigger %", s.trigger_percentage + "%"], ["Max Avg-Distance %", s.tolerance_percentage + "%"],
              ["Max Total Lots", s.maximum_total_lots], ["Max Lots/Level", s.maximum_lots_per_level],
              ["Capital Limit", inr(s.capital_limit)], ["Safety Reserve", s.safety_reserve_percentage + "%"],
              ["Auto Square-off", s.auto_square_off ? "Yes" : "No"]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-[#20242c] pb-1"><span className="text-slate-500">{k}</span><span className="mono">{v}</span></div>
            ))}
          </div>
        </div>
      </div>

      {/* Level history */}
      <div className="term-panel rounded-sm overflow-auto">
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 px-4 py-3 border-b border-[#2A2F3A]">Strategy Level History</div>
        <table className="w-full text-xs">
          <thead className="bg-[#1A1D24]">
            <tr className="text-slate-400 text-right">
              {["#", "Time", "Trig%", "Exec Spread", "Req", "Aff", "Exec Lots", "Buy Fill", "Sell Fill", "Actual Spread", "Wavg Before", "Wavg After", "Margin", "Status"].map((h) => (
                <th key={h} className={`px-2 py-2 whitespace-nowrap font-semibold ${h === "#" || h === "Time" || h === "Status" ? "text-left" : "text-right"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {levels.length === 0 && <tr><td colSpan={14} className="px-4 py-6 text-center text-slate-600">No levels executed.</td></tr>}
            {levels.map((l) => (
              <tr key={l.id} className="gz-row mono text-right">
                <td className="px-2 py-1.5 text-left">{l.level_number}</td>
                <td className="px-2 py-1.5 text-left text-slate-500">{new Date(l.executed_at).toLocaleTimeString()}</td>
                <td className="px-2 py-1.5">{l.trigger_percentage}%</td>
                <td className="px-2 py-1.5">₹{num(l.executable_spread, 2)}</td>
                <td className="px-2 py-1.5">{l.required_lots}</td>
                <td className="px-2 py-1.5">{l.affordable_lots}</td>
                <td className="px-2 py-1.5 text-[#5b9bff] font-semibold">{l.executed_lots}</td>
                <td className="px-2 py-1.5 text-[#2563EB]">₹{num(l.buy_average_fill, 2)}</td>
                <td className="px-2 py-1.5 text-[#FF6b62]">₹{num(l.sell_average_fill, 2)}</td>
                <td className="px-2 py-1.5">₹{num(l.actual_spread, 2)}</td>
                <td className="px-2 py-1.5 text-slate-500">₹{num(l.weighted_average_before, 2)}</td>
                <td className="px-2 py-1.5 font-semibold">₹{num(l.weighted_average_after, 2)}</td>
                <td className="px-2 py-1.5">{inr(l.incremental_margin)}</td>
                <td className="px-2 py-1.5 text-left"><span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#00C805]/15 text-[#00C805]">{l.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <style>{`.btn-a{display:inline-flex;align-items:center;gap:4px;padding:6px 10px;border-radius:2px;background:#1A1D24;border:1px solid #2A2F3A;font-weight:600;font-size:12px}.btn-a:hover{background:#232730}.btn-danger{display:inline-flex;align-items:center;gap:4px;padding:6px 10px;border-radius:2px;background:rgba(255,59,48,.15);color:#FF6b62;border:1px solid rgba(255,59,48,.4);font-weight:700;font-size:12px}`}</style>
    </div>
  );
}
